import { fetchWithTimeout, parseErrorResponse, BULK_FETCH_TIMEOUT } from './base.ts';

const INVITE_TIMEOUT = 15000; // 15s — invite involves email sending

export const collaborationApi = {
  // --- COLLABORATION ---
  getProjectMembers: async (projectId: string): Promise<any[]> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/members`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  inviteMember: async (
    projectId: string,
    email: string,
    role: 'editor' | 'viewer',
    message?: string
  ): Promise<void> => {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/members/invite`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ inviteeEmail: email, role, ...(message ? { message } : {}) }),
      },
      INVITE_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
  },

  updateMemberRole: async (projectId: string, memberId: string, role: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/members/${memberId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  removeMember: async (projectId: string, memberId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  leaveProject: async (projectId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/members/me`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  getUserPreferences: async (projectId: string): Promise<Record<string, any>> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/preferences`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  saveUserPreferences: async (
    projectId: string,
    preferences: Record<string, any>
  ): Promise<Record<string, any>> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/preferences`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(preferences),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  transferOwnership: async (projectId: string, newOwnerId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/transfer-ownership`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ newOwnerId }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  getPendingInvitations: async (): Promise<any[]> => {
    const res = await fetchWithTimeout('/api/invitations/pending');
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  getProjectInvitations: async (projectId: string): Promise<any[]> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/invitations`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  resendInvitation: async (invitationId: string): Promise<void> => {
    const res = await fetchWithTimeout(
      `/api/invitations/${invitationId}/resend`,
      {
        method: 'POST',
      },
      INVITE_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
  },

  revokeInvitation: async (invitationId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/invitations/${invitationId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  acceptInvitation: async (token: string): Promise<{ project_id?: string }> => {
    const res = await fetchWithTimeout(`/api/invitations/${token}/accept`, {
      method: 'POST',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  declineInvitation: async (token: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/invitations/${token}/decline`, {
      method: 'POST',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  getNotifications: async (options?: {
    limit?: number;
    offset?: number;
    filter?: 'unread' | 'read';
    type?: string;
  }): Promise<{ notifications: any[]; total: number }> => {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    if (options?.filter) params.set('filter', options.filter);
    if (options?.type) params.set('type', options.type);
    const qs = params.toString();
    const res = await fetchWithTimeout(`/api/notifications${qs ? `?${qs}` : ''}`);
    if (!res.ok) await parseErrorResponse(res);
    const data = await res.json();
    return { notifications: data.notifications || [], total: data.total ?? 0 };
  },

  markNotificationRead: async (notificationId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/notifications/${notificationId}/read`, {
      method: 'PATCH',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  markAllNotificationsRead: async (): Promise<void> => {
    const res = await fetchWithTimeout('/api/notifications/read-all', {
      method: 'PATCH',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  deleteNotification: async (notificationId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/notifications/${notificationId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  deleteReadNotifications: async (): Promise<number> => {
    const res = await fetchWithTimeout('/api/notifications/read', {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
    const data = await res.json();
    return data.deleted || 0;
  },

  getComments: async (projectId: string, entityType: string, entityId: string): Promise<any[]> => {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/comments?entityType=${entityType}&entityId=${entityId}`
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  addComment: async (comment: {
    project_id: string;
    entityType: string;
    entityId: string;
    content: string;
    parentCommentId: string | null;
    mentions: string[];
  }): Promise<void> => {
    const res = await fetchWithTimeout(`/api/projects/${comment.project_id}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(comment),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  deleteComment: async (projectId: string, commentId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/comments/${commentId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },
};
